/**
 * Merchant Pro RBAC Sub-phase C3 — Staff Users page.
 *
 * Route: /owner/staff/users
 * Access: capability `staff.view` (effectively Brand Owner +
 *   super_admin per C1 seed).
 *
 * Responsibilities:
 *   • List staff users (brand_owner / cashier / game_master) in the
 *     actor's brand. Filter by role + status (active / inactive).
 *   • Create new staff user with optional `assigned_role_id` +
 *     `assigned_branch_ids`.
 *   • Edit name, assigned role, branches, is_active. Block self-
 *     deactivate (backend also enforces).
 *
 * Tier-1 invariant: no JSX equality checks on role. Capability gating
 * via `<RequirePermission>` only; role-driven UX (branch picker
 * visibility) uses `Array.includes` membership not `===`.
 */
import { useMemo, useState } from 'react';
import { Plus, Pencil, RefreshCw, UserCog, ShieldOff } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import RequirePermission from '@/lib/capabilities';

const USERS_QK = ['rbac', 'staff', 'users'];
const ROLES_QK = ['rbac', 'staff', 'roles'];
const BRANCHES_QK = ['owner', 'brand', 'branches'];

const BASE_ROLE_LABELS = {
  brand_owner: 'Brand Owner',
  cashier:     'Cashier',
  game_master: 'Game Master',
};

// Roles that get the branch-multiselect picker in the create/edit
// modal. Membership check is intentional — keeps the Tier-1 grep
// (role-equality JSX) at zero across this file.
const ROLES_WITH_BRANCHES = ['cashier'];

function Pill({ children, tone = 'gray' }) {
  const palette = {
    gray:  'bg-white/5 text-white/70 border-white/10',
    gold:  'bg-gold/10 text-gold border-gold/30',
    red:   'bg-red-500/10 text-red-300 border-red-500/30',
    green: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-widest font-semibold border ${palette}`}>
      {children}
    </span>
  );
}

function CreateUserModal({ open, roles, branches, onClose, onCreate, busy }) {
  const [form, setForm] = useState({
    email: '', password: '', name: '',
    role: 'cashier', assigned_role_id: '', assigned_branch_ids: [],
  });
  if (!open) return null;
  const compatibleRoles = roles.filter((r) => r.base_role_enum === form.role && r.is_active !== false);
  return (
    <div data-testid="create-user-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#15151B] p-6 space-y-3">
        <h3 className="text-lg font-semibold text-white">Create staff user</h3>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="create-user-email"
            className="col-span-2 px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
          <input
            type="text"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="create-user-name"
            className="px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
          <input
            type="password"
            placeholder="Password (≥8)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="create-user-password"
            className="px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold/40"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value, assigned_role_id: '' })}
            data-testid="create-user-role"
            className="px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white"
          >
            <option value="cashier">Cashier</option>
            <option value="game_master">Game Master</option>
            <option value="brand_owner">Brand Owner</option>
          </select>
          <select
            value={form.assigned_role_id}
            onChange={(e) => setForm({ ...form, assigned_role_id: e.target.value })}
            data-testid="create-user-assigned-role"
            className="px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white"
          >
            <option value="">(default — system role for {BASE_ROLE_LABELS[form.role]})</option>
            {compatibleRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}{r.is_system_default ? ' · system' : ''}
              </option>
            ))}
          </select>
        </div>
        {ROLES_WITH_BRANCHES.includes(form.role) && branches.length > 0 && (
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 font-semibold">
              Branches (cashiers only)
            </label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {branches.map((b) => {
                const on = form.assigned_branch_ids.includes(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      assigned_branch_ids: on
                        ? form.assigned_branch_ids.filter((x) => x !== b.id)
                        : [...form.assigned_branch_ids, b.id],
                    })}
                    data-testid={`create-user-branch-${b.id}`}
                    className={`px-2.5 py-1 rounded-lg text-xs border ${
                      on
                        ? 'bg-gold/15 text-gold border-gold/40'
                        : 'bg-[#0E0E14] text-white/70 border-white/10 hover:bg-white/5'
                    }`}
                  >
                    {b.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-white/70 hover:bg-white/5 rounded-xl border border-white/10">Cancel</button>
          <button
            type="button"
            onClick={() => onCreate(form)}
            data-testid="create-user-submit"
            disabled={busy || !form.email || !form.password || !form.name}
            className="px-4 py-2 text-sm font-semibold rounded-xl bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ open, user, roles, branches, currentUserId, onClose, onSave, busy }) {
  const [form, setForm] = useState({
    name: '', assigned_role_id: '', assigned_branch_ids: [], is_active: true,
  });
  const opened = open && user;
  useMemo(() => {
    if (opened) {
      setForm({
        name: user.name || '',
        assigned_role_id: user.assigned_role_id || '',
        assigned_branch_ids: user.assigned_branch_ids || [],
        is_active: user.is_active !== false,
      });
    }
  }, [opened, user]);
  if (!open || !user) return null;
  const compatibleRoles = roles.filter((r) => r.base_role_enum === user.role && r.is_active !== false);
  const isSelf = user.id === currentUserId;
  return (
    <div data-testid="edit-user-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#15151B] p-6 space-y-3">
        <h3 className="text-lg font-semibold text-white">
          Edit {user.name} <span className="text-white/40 text-sm font-normal">· {BASE_ROLE_LABELS[user.role]}</span>
        </h3>
        <input
          type="text"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="edit-user-name"
          className="w-full px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white"
        />
        <select
          value={form.assigned_role_id}
          onChange={(e) => setForm({ ...form, assigned_role_id: e.target.value })}
          data-testid="edit-user-assigned-role"
          className="w-full px-3 py-2 rounded-xl bg-[#0E0E14] border border-white/10 text-sm text-white"
        >
          <option value="">(default — system role for {BASE_ROLE_LABELS[user.role]})</option>
          {compatibleRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}{r.is_system_default ? ' · system' : ''}
            </option>
          ))}
        </select>
        {ROLES_WITH_BRANCHES.includes(user.role) && branches.length > 0 && (
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 font-semibold">
              Branches
            </label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {branches.map((b) => {
                const on = form.assigned_branch_ids.includes(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      assigned_branch_ids: on
                        ? form.assigned_branch_ids.filter((x) => x !== b.id)
                        : [...form.assigned_branch_ids, b.id],
                    })}
                    data-testid={`edit-user-branch-${b.id}`}
                    className={`px-2.5 py-1 rounded-lg text-xs border ${
                      on
                        ? 'bg-gold/15 text-gold border-gold/40'
                        : 'bg-[#0E0E14] text-white/70 border-white/10 hover:bg-white/5'
                    }`}
                  >
                    {b.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-white/80">
          <input
            type="checkbox"
            checked={form.is_active}
            disabled={isSelf}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            data-testid="edit-user-is-active"
            className="w-4 h-4 rounded border-white/20"
          />
          Active {isSelf && <span className="text-xs text-white/40">(can&apos;t deactivate self)</span>}
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-white/70 hover:bg-white/5 rounded-xl border border-white/10">Cancel</button>
          <button
            type="button"
            onClick={() => onSave(form)}
            data-testid="edit-user-save"
            disabled={busy || !form.name}
            className="px-4 py-2 text-sm font-semibold rounded-xl bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StaffUsersPage() {
  const { token, user: currentUser } = useAuthStore();
  const qc = useQueryClient();
  const [filter, setFilter] = useState({ role: '', status: 'active' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const { data: usersData, isLoading } = useQuery({
    queryKey: [...USERS_QK, filter],
    queryFn: () => api.ownerStaffUsers(token, {
      role: filter.role || undefined,
      status: filter.status || undefined,
    }),
    enabled: !!token,
  });
  const { data: rolesData } = useQuery({
    queryKey: ROLES_QK,
    queryFn: () => api.ownerStaffRoles(token),
    enabled: !!token,
  });
  const { data: brandData } = useQuery({
    queryKey: BRANCHES_QK,
    queryFn: () => api.ownerBrand(token),
    enabled: !!token,
  });
  const branches = brandData?.branches || [];

  const users = usersData?.items || [];
  const roles = rolesData?.items || [];

  const createM = useMutation({
    mutationFn: (body) => api.ownerCreateStaffUser(token, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_QK });
      setCreateOpen(false);
    },
  });
  const editM = useMutation({
    mutationFn: ({ userId, body }) => api.ownerPatchStaffUser(token, userId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_QK });
      setEditTarget(null);
    },
  });

  return (
    <div className="space-y-6" data-testid="staff-users-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Staff Users</h1>
          <p className="text-sm text-white/55 mt-1">
            Manage staff accounts and assign custom roles. Custom-role assignment is bound by
            base-role compatibility (cannot cross enums).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filter.role}
            onChange={(e) => setFilter({ ...filter, role: e.target.value })}
            data-testid="users-filter-role"
            className="px-3 py-2 rounded-xl bg-[#15151B] border border-white/10 text-sm text-white/90"
          >
            <option value="">All roles</option>
            <option value="brand_owner">Brand Owner</option>
            <option value="cashier">Cashier</option>
            <option value="game_master">Game Master</option>
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            data-testid="users-filter-status"
            className="px-3 py-2 rounded-xl bg-[#15151B] border border-white/10 text-sm text-white/90"
          >
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button
            type="button"
            onClick={() => qc.invalidateQueries({ queryKey: USERS_QK })}
            data-testid="staff-users-refresh"
            className="px-3 py-2 rounded-xl border border-white/10 text-white/80 hover:bg-white/5 inline-flex items-center gap-2"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <RequirePermission cap="staff.assign_user">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              data-testid="staff-users-new"
              className="px-3 py-2 rounded-xl bg-gold/15 text-gold border border-gold/40 hover:bg-gold/25 inline-flex items-center gap-2 text-sm font-semibold"
            >
              <Plus size={14} /> New user
            </button>
          </RequirePermission>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#15151B] overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="staff-users-table">
          <thead className="bg-[#0E0E14] text-xs uppercase tracking-widest text-white/55">
            <tr>
              <th className="px-5 py-3 text-left font-semibold">User</th>
              <th className="px-5 py-3 text-left font-semibold">Role</th>
              <th className="px-5 py-3 text-left font-semibold">Assigned role</th>
              <th className="px-5 py-3 text-left font-semibold">Branches</th>
              <th className="px-5 py-3 text-left font-semibold">Status</th>
              <th className="px-5 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading && <tr><td colSpan={6} className="text-center py-8 text-white/55">Loading…</td></tr>}
            {!isLoading && users.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-white/40">No staff users match this filter.</td></tr>
            )}
            {!isLoading && users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-white/[0.03]">
                <td className="px-5 py-3">
                  <div className="font-medium text-white/95">{u.name}</div>
                  <div className="text-xs text-white/55">{u.email}</div>
                </td>
                <td className="px-5 py-3 text-white/70">{BASE_ROLE_LABELS[u.role]}</td>
                <td className="px-5 py-3">
                  {u.assigned_role_name ? (
                    <Pill tone="gold">{u.assigned_role_name}</Pill>
                  ) : (
                    <span className="text-xs text-white/40">— default —</span>
                  )}
                </td>
                <td className="px-5 py-3 text-white/70 text-xs">
                  {u.assigned_branch_ids.length === 0
                    ? <span className="text-white/40">all / —</span>
                    : <span>{u.assigned_branch_ids.length} branch{u.assigned_branch_ids.length === 1 ? '' : 'es'}</span>}
                </td>
                <td className="px-5 py-3">
                  {u.is_active
                    ? <Pill tone="green">Active</Pill>
                    : <Pill tone="red"><ShieldOff size={10} /> Inactive</Pill>}
                </td>
                <td className="px-5 py-3 text-right">
                  <RequirePermission cap="staff.assign_user">
                    <button
                      type="button"
                      onClick={() => setEditTarget(u)}
                      data-testid={`user-row-edit-${u.id}`}
                      className="px-2.5 py-1 text-xs rounded-lg border border-white/10 hover:bg-white/5 text-white/80 inline-flex items-center gap-1"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  </RequirePermission>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateUserModal
        open={createOpen}
        roles={roles}
        branches={branches}
        busy={createM.isPending}
        onClose={() => setCreateOpen(false)}
        onCreate={(body) => createM.mutate(body)}
      />
      <EditUserModal
        open={!!editTarget}
        user={editTarget}
        roles={roles}
        branches={branches}
        currentUserId={currentUser?.id}
        busy={editM.isPending}
        onClose={() => setEditTarget(null)}
        onSave={(body) => editM.mutate({ userId: editTarget.id, body })}
      />
    </div>
  );
}
